import {
  makeObservable,
  observable,
  action,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
  computed,
  override,
} from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import {
  IndexedSpaceAccountCalendarEventSyncUpdateValue,
  SpaceAccountCalendarEventIndexData,
  SpaceAccountCalendarEventModelData,
} from "@/store/calendar/space-account-calendar-event/types";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { Uuid } from "@/domains/global/identifiers";
import { liveQuery, Subscription, Table } from "dexie";
import { DateTime } from "luxon";
import { Maybe } from "@/domains/common/types";
import { AppStore } from "@/store/AppStore";
import { getOngoingAndUpcomingCalendarEvents } from "@/store/calendar/space-account-calendar-event/search-calendar-events";
import { MIN_ATTENDEE_COUNT } from "@/store/calendar/space-account-calendar-event/helpers";

export class AppStoreSpaceAccountCalendarEventStore extends BaseSyncModelStore<
  SpaceAccountCalendarEventObservable,
  SpaceAccountCalendarEventModelData
> {
  liveQuerySubscription?: Subscription;
  ongoingAndUpcomingEventsObserverCount = 0;

  ongoingEventIds: Uuid[] = [];
  upcomingEventIds: Uuid[] = [];

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountCalendarEvent, ...injectedDeps });
    makeObservable<
      this,
      "subscribeToOngoingAndUpcomingEvents" | "unsubscribeFromOngoingAndUpcomingEvents"
    >(this, {
      createSyncModel: false,
      computeIndexes: false,
      localTable: override,

      liveQuerySubscription: observable,
      ongoingAndUpcomingEventsObserverCount: observable,
      subscribeToOngoingAndUpcomingEvents: action,
      unsubscribeFromOngoingAndUpcomingEvents: action,

      ongoingEventIds: observable,
      upcomingEventIds: observable,
      ongoingEvents: computed,
      upcomingEvents: computed,
      ongoingAndUpcomingEvents: computed,
      nextUpcomingEventWithNoNote: computed,
    });

    onBecomeObserved(this, "ongoingEventIds", () => {
      runInAction(() => this.ongoingAndUpcomingEventsObserverCount++);
      if (this.ongoingAndUpcomingEventsObserverCount === 1) {
        this.subscribeToOngoingAndUpcomingEvents();
      }
    });
    onBecomeUnobserved(this, "ongoingEventIds", () => {
      runInAction(() => this.ongoingAndUpcomingEventsObserverCount--);
      if (this.ongoingAndUpcomingEventsObserverCount === 0) {
        this.unsubscribeFromOngoingAndUpcomingEvents();
      }
    });
    onBecomeObserved(this, "upcomingEventIds", () => {
      runInAction(() => this.ongoingAndUpcomingEventsObserverCount++);
      if (this.ongoingAndUpcomingEventsObserverCount === 1) {
        this.subscribeToOngoingAndUpcomingEvents();
      }
    });
    onBecomeUnobserved(this, "upcomingEventIds", () => {
      runInAction(() => this.ongoingAndUpcomingEventsObserverCount--);
      if (this.ongoingAndUpcomingEventsObserverCount === 0) {
        this.unsubscribeFromOngoingAndUpcomingEvents();
      }
    });
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountCalendarEventModelData>
  ): SpaceAccountCalendarEventObservable {
    return new SpaceAccountCalendarEventObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind]
      .local as Table<IndexedSpaceAccountCalendarEventSyncUpdateValue>;
  }

  get ongoingEvents(): SpaceAccountCalendarEventObservable[] {
    const ongoingEventIds = this.ongoingEventIds;
    if (ongoingEventIds.length === 0) return [];
    return this.getMany(ongoingEventIds);
  }

  get upcomingEvents(): SpaceAccountCalendarEventObservable[] {
    const upcomingEventIds = this.upcomingEventIds;
    if (upcomingEventIds.length === 0) return [];
    return this.getMany(upcomingEventIds);
  }

  get ongoingAndUpcomingEvents(): SpaceAccountCalendarEventObservable[] {
    return [...this.ongoingEvents, ...this.upcomingEvents];
  }

  get nextUpcomingEventWithNoNote(): SpaceAccountCalendarEventObservable | undefined {
    // Three conditions:
    // A. If you have an ongoing meeting, and there's no note created for that event -> Return the ongoing meeting
    // B. If you have an ongoing meeting with a note, and there's a meeting within the next 30 minutes and there's no note created for that event -> Return the next meeting
    // C. If you don't have an ongoing meeting, there's a meeting within the next 30 minutes and there's no note created for that event -> Return the next meeting
    // Or else, this returns undefined

    // A: Check ongoing events first for one without a note
    const ongoingEventWithNoNote = this.ongoingEvents.find(event => !event.associatedNote);
    if (ongoingEventWithNoNote) return ongoingEventWithNoNote;

    // B & C: Check upcoming events within 30 minutes without a note
    // (We reach here if there's no ongoing event, or if ongoing events all have notes)
    const now = DateTime.now();
    const thirtyMinutesFromNow = now.plus({ minutes: 30 });

    const upcomingEventWithNoNote = this.upcomingEvents.find(event => {
      if (event.associatedNote) return false; // Has a note, skip
      if (!event.startTime) return false; // No start time, skip

      const startTime = DateTime.fromISO(event.startTime);
      return startTime <= thirtyMinutesFromNow;
    });

    return upcomingEventWithNoNote;
  }

  subscribeToOngoingAndUpcomingEvents() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(async () => {
      const { ongoingEventIds, upcomingEventIds } = await getOngoingAndUpcomingCalendarEvents({
        store: this.store,
        minAttendeeCount: MIN_ATTENDEE_COUNT,
      });
      return { upcomingEventIds, ongoingEventIds };
    }).subscribe({
      next: ({ upcomingEventIds, ongoingEventIds }) => {
        runInAction(() => {
          this.upcomingEventIds = upcomingEventIds;
          this.ongoingEventIds = ongoingEventIds;
        });
      },
    });
  }

  unsubscribeFromOngoingAndUpcomingEvents() {
    this.liveQuerySubscription?.unsubscribe();
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SpaceAccountCalendarEventModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SpaceAccountCalendarEventModelData>[];
  }): SpaceAccountCalendarEventIndexData {
    const data =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<SpaceAccountCalendarEventModelData>) ||
      remoteData;

    const startTime = data?.model_data?.start_time || "";
    const endTime = data?.model_data?.end_time || "";
    const startTimeUtc = startTime ? DateTime.fromISO(startTime).toUTC().toISO() || "" : "";
    const endTimeUtc = endTime ? DateTime.fromISO(endTime).toUTC().toISO() || "" : "";

    return {
      start_time: startTime,
      start_time_utc: startTimeUtc,
      end_time: endTime,
      end_time_utc: endTimeUtc,
      attendee_count: data?.model_data?.attendee_count || 0,
    };
  }
}
